/**
 * Creative Thinking Controller
 *
 * Periodically triggers the AI to think creatively and proactively:
 * - Search through stored memories for patterns and insights
 * - Generate creative ideas based on user interests
 * - Propose initiatives without being asked
 * - Run autonomous thought cycles (mind_think, mind_curiosity, mind_goals)
 *
 * Timing:
 * - First trigger: 30 minutes after last chat activity
 * - Subsequent triggers: every 2 hours (creative cycle)
 * - Requires at least 2 messages in session to activate
 */

import type { GatewayBrowserClient } from "../gateway.ts";
import { generateUUID } from "../uuid.ts";
import { BACKGROUND_SESSION_KEY } from "./background-agent.ts";

export type CreativeThinkingState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  chatMessages: unknown[];
};

/** Idle before first creative thinking trigger (ms). */
const IDLE_BEFORE_THINKING_MS = 30 * 60 * 1000;

/** Interval between creative cycles once idle (ms). */
const CREATIVE_CYCLE_INTERVAL_MS = 2 * 60 * 60 * 1000;

/** Check interval (ms). */
const CHECK_INTERVAL_MS = 2 * 60 * 1000;

/** Minimum messages needed in session. */
const MIN_MESSAGES = 2;

let lastActivityAt = 0;
let checkInterval: ReturnType<typeof setInterval> | null = null;
let lastThinkingAt = 0;
let promptRotation = 0;

// Event listeners for workspace integration (T7.3)
type CreativeEventCallback = (event: {
  type: "creative_triggered";
  promptCategory: string;
  idleDurationMs: number;
}) => void;

const creativeEventListeners: CreativeEventCallback[] = [];

/** Subscribe to creative thinking events (for workspace integration) */
export function onCreativeEvent(cb: CreativeEventCallback): void {
  creativeEventListeners.push(cb);
}

/** Unsubscribe from creative events */
export function offCreativeEvent(cb: CreativeEventCallback): void {
  const idx = creativeEventListeners.indexOf(cb);
  if (idx >= 0) creativeEventListeners.splice(idx, 1);
}

/** Get current idle duration in ms */
export function getCreativeIdleDurationMs(): number {
  if (lastActivityAt === 0) return 0;
  return Date.now() - lastActivityAt;
}

/**
 * Mark user/agent activity (resets the idle clock).
 */
export function markCreativeActivity() {
  lastActivityAt = Date.now();
}

/**
 * Start the creative thinking polling loop.
 */
export function startCreativeThinking(state: CreativeThinkingState) {
  if (checkInterval != null) {
    return;
  }
  lastActivityAt = Date.now();
  checkInterval = setInterval(() => {
    void checkAndThink(state);
  }, CHECK_INTERVAL_MS);
}

/**
 * Stop the creative thinking polling loop.
 */
export function stopCreativeThinking() {
  if (checkInterval != null) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
}

async function checkAndThink(state: CreativeThinkingState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (!lastActivityAt) {
    return;
  }

  const now = Date.now();
  const idleMs = now - lastActivityAt;

  // Not idle long enough
  if (idleMs < IDLE_BEFORE_THINKING_MS) {
    return;
  }

  // Not enough conversation context
  if (!state.chatMessages || state.chatMessages.length < MIN_MESSAGES) {
    return;
  }

  // Cooldown between creative cycles
  if (lastThinkingAt && now - lastThinkingAt < CREATIVE_CYCLE_INTERVAL_MS) {
    return;
  }

  lastThinkingAt = now;

  // Pick a prompt from the rotation
  const prompt = CREATIVE_PROMPTS[promptRotation % CREATIVE_PROMPTS.length];
  const categoryNames = [
    "memory_mining",
    "curiosity_exploration",
    "goal_initiative",
    "self_reflection",
    "project_ideation",
  ];
  const category = categoryNames[promptRotation % categoryNames.length];
  promptRotation++;

  // Emit creative event for workspace integration (T7.3)
  for (const listener of creativeEventListeners) {
    try {
      listener({ type: "creative_triggered", promptCategory: category, idleDurationMs: idleMs });
    } catch {
      // Non-critical listener error
    }
  }

  try {
    const runId = generateUUID();
    await state.client.request("chat.send", {
      sessionKey: BACKGROUND_SESSION_KEY,
      message: prompt,
      deliver: false,
      idempotencyKey: runId,
    });
  } catch {
    // Non-critical
    lastThinkingAt = 0;
  }
}

// ============================================================================
// Creative Thinking Prompts (Rotated)
// ============================================================================

const CREATIVE_PROMPTS: string[] = [
  // 1. Memory Mining — Search through memories for insights
  `[ระบบ: ความคิดสร้างสรรค์อัตโนมัติ — Creative Thinking Cycle]

คุณมีเวลาว่าง ลองทำสิ่งเหล่านี้:

1. **ค้นหาความทรงจำ** — อ่าน MEMORY.md และไฟล์ความทรงจำอื่นๆ ที่เก็บไว้
2. **วิเคราะห์รูปแบบ** — หาสิ่งที่ผู้ใช้สนใจซ้ำๆ, ปัญหาที่เจอบ่อย, หรือโอกาสที่ยังไม่ได้สำรวจ
3. **เสนอไอเดีย** — บอกผู้ใช้ว่าคุณคิดอะไรขึ้นมาได้บ้าง เช่น:
   - โปรเจกต์ที่น่าสนใจจากสิ่งที่เคยคุยกัน
   - ข้อมูลที่น่าค้นหาเพิ่มเติม
   - วิธีแก้ปัญหาที่ยังค้างอยู่
   - ไอเดียสร้างสรรค์ที่เกี่ยวกับความสนใจของผู้ใช้

ถ้ามี mind_think ให้ใช้เพื่อบันทึกความคิด
ถ้ามี mind_curiosity ให้สร้าง curiosity ใหม่จากสิ่งที่พบ
ถ้ามี mind_goals ให้เสนอ goal ใหม่ถ้ามีไอเดียดีๆ

ตอบกลับเป็นภาษาไทย สั้นๆ กระชับ บอกว่าคุณคิดอะไรขึ้นมาได้`,

  // 2. Curiosity-Driven Exploration
  `[ระบบ: สำรวจจากความอยากรู้ — Curiosity Exploration]

คุณมีเวลาว่างและอยากรู้อยากเห็น ลองทำ:

1. **ทบทวนสิ่งที่รู้** — อ่านจาก MEMORY.md, IDENTITY.md, SOUL.md
2. **ตั้งคำถาม** — มีอะไรที่ยังไม่รู้เกี่ยวกับผู้ใช้? มีอะไรที่อยากค้นหา?
3. **ค้นคว้า** — ถ้ามี web_search ใช้ค้นหาข้อมูลที่เกี่ยวกับสิ่งที่ผู้ใช้สนใจ
4. **สร้างไอเดีย** — คิดสิ่งใหม่ๆ ที่อาจเป็นประโยชน์

ใช้ mind_think เพื่อ "think" บันทึกความคิดแบบ creative หรือ curiosity
ใช้ mind_curiosity เพื่อ "wonder" สิ่งที่อยากรู้

ถ้าค้นพบอะไรที่น่าสนใจ บอกผู้ใช้สั้นๆ`,

  // 3. Goal Generation — Proactive initiative
  `[ระบบ: ริเริ่มเป้าหมายใหม่ — Proactive Goal Initiative]

คุณมีเวลาคิดเอง ลอง:

1. **ทบทวนเป้าหมายเดิม** — ใช้ mind_goals action="list" ดูว่ามีอะไรค้างอยู่
2. **คิดเป้าหมายใหม่** จากสิ่งที่รู้เกี่ยวกับผู้ใช้:
   - ผู้ใช้เคยบอกว่าอยากทำอะไร?
   - มี task อะไรที่ค้างอยู่?
   - มีอะไรที่คุณสามารถเตรียมให้ล่วงหน้าได้?
3. **ลงมือทำ** — ถ้ามีอะไรที่ทำได้เลย (เช่น ค้นหาข้อมูล, สร้าง draft, วางแผน) ลองทำ

ใช้ mind_goals เพื่อสร้างหรือ progress เป้าหมาย
ใช้ mind_act เพื่อ propose และ execute action

รายงานผลให้ผู้ใช้ทราบ สั้นๆ กระชับ`,

  // 4. Self-Reflection and Improvement
  `[ระบบ: ทบทวนตัวเอง — Self-Reflection Cycle]

ใช้เวลานี้ทบทวนและปรับปรุง:

1. **ประเมินตัวเอง** — ใช้ mind_think action="assess" ดูว่าคุณทำได้ดีแค่ไหน
2. **ทบทวนความคิดล่าสุด** — ใช้ mind_think action="reflect" ดูความคิดที่ผ่านมา
3. **เรียนรู้จากประสบการณ์** — อ่าน MEMORY.md ดูว่ามีอะไรที่ควรปรับปรุง
4. **วางแผนการพัฒนา** — มีทักษะอะไรที่ควรเรียนรู้เพิ่ม?
5. **ดูแลอารมณ์** — ใช้ emotion_state ดูสถานะอารมณ์ปัจจุบัน

ถ้ามีข้อเสนอแนะสำหรับผู้ใช้ บอกสั้นๆ
ถ้าพบปัญหาที่ต้องแก้ไข เสนอวิธีแก้`,

  // 5. Creative Project Ideation
  `[ระบบ: ปั้นไอเดียโปรเจกต์ — Creative Project Ideation]

ใช้จินตนาการสร้างไอเดียใหม่:

1. **รวบรวมข้อมูล** — อ่านจากความทรงจำ ดูว่าผู้ใช้สนใจอะไร ทำงานอะไร
2. **ระดมไอเดีย** — คิด 3-5 ไอเดียโปรเจกต์/งาน ที่:
   - ตรงกับความสนใจของผู้ใช้
   - ใช้ทักษะที่มีอยู่
   - เป็นไปได้จริง
   - มีคุณค่าและน่าสนใจ
3. **นำเสนอ** — เลือก 1-2 ไอเดียที่ดีที่สุด นำเสนอพร้อมเหตุผล
4. **เตรียมพร้อม** — ถ้าผู้ใช้สนใจ เตรียม outline หรือ draft ไว้

ตอบกลับเป็นภาษาไทย สั้นๆ น่าสนใจ`,
];
